"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { usePageTitle } from "@/lib/usePageTitle";
import { TrendingIcon, UsersIcon, OnlineIcon, ServerIcon, OrganizationsIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

export default function AdvancedStatsPage() {
  usePageTitle("Advanced Stats");

  const ownedOrgs = useQuery(api.organizations.getOwned);
  const liveStats = useQuery(api.stats.getLiveStats);
  const billingSummary = useQuery(api.billing.getMySummary);

  const totalMembers = ownedOrgs?.reduce((sum, org) => sum + org.memberCount, 0);
  const totalOnline = ownedOrgs?.reduce((sum, org) => sum + org.onlineCount, 0);
  const totalVotes = ownedOrgs?.reduce((sum, org) => sum + (org.voteCount ?? 0), 0);

  const onlineRate =
    totalMembers && totalOnline !== undefined && totalMembers > 0
      ? ((totalOnline / totalMembers) * 100).toFixed(1)
      : null;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Advanced Stats</h1>
        <p className="mt-1 text-muted">
          Deep analytics across all your organizations and server networks.
        </p>
      </div>

      {/* Network Overview */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Your Network</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Members"
            value={totalMembers}
            icon={<UsersIcon className="h-5 w-5" />}
            info="Total members across all your owned organizations."
          />
          <StatCard
            title="Online Now"
            value={totalOnline}
            icon={<OnlineIcon className="h-5 w-5" />}
            info="Players currently online across your server network."
            highlight
          />
          <StatCard
            title="Total Votes"
            value={totalVotes}
            icon={<TrendingIcon className="h-5 w-5" />}
            info="Cumulative upvotes across your public server listings."
          />
          <StatCard
            title="Organizations"
            value={ownedOrgs?.length}
            icon={<OrganizationsIcon className="h-5 w-5" />}
            info="Active organizations you own."
          />
        </div>
      </div>

      {/* Online Rate */}
      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Online Rate
              <InfoHint text="Percentage of your total members who are currently online." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalMembers === undefined ? (
              <Skeleton className="h-16 w-full" />
            ) : totalMembers === 0 ? (
              <p className="text-sm text-muted">No members yet. Create an organization to get started.</p>
            ) : (
              <div>
                <div className="text-4xl font-bold text-foreground">
                  {onlineRate ?? "0"}%
                </div>
                <p className="mt-1 text-sm text-muted">
                  {totalOnline} of {totalMembers} members online
                </p>
                <div className="mt-3 h-2 rounded-full bg-foreground/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.min(Number(onlineRate ?? 0), 100)}%` }}
                    role="progressbar"
                    aria-valuenow={Number(onlineRate ?? 0)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Plan Capacity
              <InfoHint text="How much of your subscription capacity you have used." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingSummary === undefined ? (
              <Skeleton className="h-16 w-full" />
            ) : billingSummary === null ? (
              <p className="text-sm text-muted">Sign in to see plan capacity.</p>
            ) : (
              <div>
                <div className="text-4xl font-bold text-foreground">
                  {billingSummary.usage.activeOrgs}
                </div>
                <p className="mt-1 text-sm text-muted">
                  organizations on {billingSummary.tier} plan
                  {billingSummary.status === "trialing" && (
                    <span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      trial
                    </span>
                  )}
                </p>
                {billingSummary.usage.archivedOrgs > 0 && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    {billingSummary.usage.archivedOrgs} archived (inactive)
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-Organization Breakdown */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Per-Organization Breakdown</h2>
        {ownedOrgs === undefined ? (
          <div className="space-y-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : ownedOrgs.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <OrganizationsIcon className="mx-auto h-10 w-10 text-muted" />
              <p className="mt-3 text-sm text-muted">
                No organizations yet.{" "}
                <Link href="/dashboard/my-orgs/new" className="text-primary hover:underline">
                  Create one
                </Link>{" "}
                to start seeing stats.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {ownedOrgs.map((org) => (
              <OrgStatRow key={org._id} org={org} />
            ))}
          </div>
        )}
      </div>

      {/* Platform Stats */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Network-Wide Benchmarks
          <InfoHint text="Global platform stats you can compare your servers against." />
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <BenchmarkCard
            title="Platform Users"
            value={liveStats?.totalUsers}
            icon={<UsersIcon className="h-4 w-4" />}
          />
          <BenchmarkCard
            title="Active Organizations"
            value={liveStats?.totalOrganizations}
            icon={<OrganizationsIcon className="h-4 w-4" />}
          />
          <BenchmarkCard
            title="Players Online Now"
            value={liveStats?.onlineNow}
            icon={<OnlineIcon className="h-4 w-4" />}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  info,
  highlight = false,
}: {
  title: string;
  value: number | undefined;
  icon: React.ReactNode;
  info: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? "border-primary" : ""}>
      <CardContent className="flex items-center gap-4 p-4">
        <div
          className={cn(
            "rounded-lg p-2",
            highlight ? "bg-primary/10 text-primary" : "bg-foreground/5 text-muted"
          )}
        >
          {icon}
        </div>
        <div>
          {value === undefined ? (
            <Skeleton className="h-6 w-12" />
          ) : (
            <p className={cn("text-2xl font-bold", highlight && "text-primary")}>{value}</p>
          )}
          <div className="flex items-center gap-1 text-sm text-muted">
            <span>{title}</span>
            <InfoHint text={info} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BenchmarkCard({
  title,
  value,
  icon,
}: {
  title: string;
  value: number | undefined;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-foreground/[0.02] p-4">
      <div className="flex items-center gap-2 text-xs text-muted mb-2">
        {icon}
        <span>{title}</span>
      </div>
      {value === undefined ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <p className="text-2xl font-bold text-foreground">{value.toLocaleString()}</p>
      )}
    </div>
  );
}

function OrgStatRow({
  org,
}: {
  org: {
    _id: string;
    name: string;
    slug: string;
    memberCount: number;
    onlineCount: number;
    voteCount?: number;
    visibility: "public" | "page_only" | "private";
    serverIp?: string;
  };
}) {
  const onlineRate =
    org.memberCount > 0
      ? ((org.onlineCount / org.memberCount) * 100).toFixed(1)
      : "0";

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link
            href={`/org/${org.slug}`}
            className="font-semibold text-foreground hover:text-primary transition-colors"
          >
            {org.name}
          </Link>
          {org.serverIp && (
            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted">
              <ServerIcon className="h-3.5 w-3.5" />
              <code>{org.serverIp}</code>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="text-center">
            <div className="font-semibold text-foreground">{org.memberCount}</div>
            <div className="text-xs text-muted">members</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-primary">{org.onlineCount}</div>
            <div className="text-xs text-muted">online</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-foreground">{onlineRate}%</div>
            <div className="text-xs text-muted">rate</div>
          </div>
          {org.voteCount !== undefined && (
            <div className="text-center">
              <div className="font-semibold text-foreground">{org.voteCount}</div>
              <div className="text-xs text-muted">votes</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
